// BottomBar (UI-C10, UI-P2): the large central mute in the thumb zone, the Listen toggle, the seat count
// (opens the seat list) and leave. The mic button carries the MicLevelRing (UI-C19, EM-12).
// MAJLIS-62, consultant rulings 2026-09-23: in the ring's status area (NOT the banner slot above the canvas), the
// D-38 hint "Live audio paused while you replay" for exactly the replay gate's span (ruling 1, UI-C26 to be written),
// and the one-line "Tap to hear the room" prompt only while a voice's play() was refused (ruling 2, UI-C27 to be
// written; unmeasured on iOS).
// MAJLIS-64 (D-43, approved mock-ups): the Listen toggle keeps its place, icon and name (UI-C21); in a room where
// interpreter mode is offered a pill beside the bar names the listen mode and opens the Listen sheet, with one quiet
// line that states the ducking ("live voices lowered") - or, at the spend cap, "text only, voice on tap". While a
// spoken translation holds the mic gate, the ring's status line is UI-C24's hint, in both languages (the mock-up).
// MAJLIS-88 (UI-C29, owner-approved mock-up frames b/c/e/f): while Listen is off and another seat speaks, the Listen
// control keeps its slash and shows activity in that seat's colour (arcs behind the slash, a ring from the disc), its
// accessible name carries the state, and the first time per room a hint above it says "Tap to hear them" (aria-hidden,
// so it is not read twice). RoomScreen decides WHEN (the rule); this draws it.
// MAJLIS-71 (D-52, the approved mock-up mic-stopped-mockup.html): the `stopped` ring - the microphone's track ended and no
// re-open has succeeded - is a solid amber (--warn) ring with the MicOff glyph, the hint "Microphone stopped. Tap to turn
// it back on" in the ring's own status slot, and the accessible name "Turn the microphone back on", because the tap
// re-opens the microphone (and unmutes) instead of toggling mute. aria-pressed is his own mute, so false there.
// MAJLIS-136 (T-162, consultant ruling 2026-09-26): with the microphone `denied` or `unavailable` the mute is DISABLED
// and a tap sends nothing - there is nothing local to mute, and the P-3 mute/unmute would only flip the seat's server
// record that other seats see. aria-disabled, not the `disabled` attribute: the button stays visible and in the focus
// order, so the micDenied / micUnavailable line it is described by (aria-describedby) is still reached from it; the
// click is dropped HERE, which is what makes it inert. `stopped` is not blocked: its tap re-opens the microphone.
// MAJLIS-137 (D-57, UI-C39; the passive-mode mock-up (c)/(d)): in a PASSIVE room the bar's start side holds ONLY the
// "Auto-play translations" switch (today's interpreter pill, .listen-mode/.listen-pill, as role=switch) in place of the
// Listen button and the seat count; Mute and Leave stay. Where the full label would wrap (a narrow phone: measured, see
// app.css) the pill shows "Auto-play"; the switch's accessible name is always the full words. Its hint - "Translations
// are silent..." - sits in the mute line's own slot (.mic-status, above the buttons), with a close, and yields that slot
// to the microphone's own line while one shows.
// MAJLIS-163 (UI-C10 amended; the owner-approved room-controls mock-up, frames (a) and (e)): every bar item carries a
// one-word label under it - Listen, People with the count, Mute - and Leave is a red pill with the hang-up handset and
// the word, the only red on the screen (--leave / --on-leave). The People control is a round disc like Listen; its
// count moved into its label. The labels are aria-hidden: each button's accessible name already says the same.
// MAJLIS-174 (component-rules.md section 5; the owner-approved screen-rules mock-up of 2026-10-05, frame (m)): every line
// above the bar is ONE status line style - 12 px, muted, centred (app.css .mic-status). The warning colour (--warn) is
// used only where a TAP is needed, and then the line names the control and the control carries a 2 px --warn ring: the
// Listen hint ("Tap Listen to hear them"; the Listen disc ringed), the stopped microphone (the mic's own --warn ring,
// MAJLIS-71) and "Tap to hear the room" (the line IS the control). What shows when is unchanged; this is look only.
import type { CSSProperties } from "react";
import { dirOf, useI18n } from "../i18n";
import { AR_BESIDE_EN } from "../i18n/bilingual";
import { en } from "../i18n/catalogues/en";
import { shown } from "../i18n/pending";
import type { RingState } from "../media/micRing";
import type { LiveUnder } from "../prefs";
import type { ListenMode } from "../protocol/types";
import {
  CloseIcon,
  HangUpIcon,
  MicIcon,
  MicOffIcon,
  SeatsIcon,
  SpeakerIcon,
  SpeakerOffActivityIcon,
  SpeakerOffIcon,
} from "./icons";

const RING_MESSAGE = {
  denied: "micDenied",
  unavailable: "micUnavailable",
  unheard: "micUnheard",
  muted: "micMuted",
  playing: "micPlaying",
  stopped: "micStopped",
} as const;

export function BottomBar({
  ring,
  level,
  muted,
  onMute,
  listening,
  listenEnabled,
  livePaused,
  tapToHear,
  onListen,
  seatCount,
  onSeats,
  onLeave,
  listenMode = null,
  onListenChoice,
  liveUnder = "lowered",
  voiceCapped = false,
  micListening = false,
  listenActivity = null,
  listenHint = false,
  passive = false,
  autoPlay = null,
  onAutoPlay,
  autoPlayHint = false,
  onDismissHint,
  playbackUnavailable = false,
}: {
  /** MAJLIS-140 (D-59, condition 5): the passive voice track could not play a clip - the switch's line, never silent. */
  playbackUnavailable?: boolean;
  /** MAJLIS-137 (UI-C39): a passive room - the start side is the auto-play switch alone (no Listen, no seat count). */
  passive?: boolean;
  /** The switch's state; null: voicing is off here, so no switch at all. */
  autoPlay?: boolean | null;
  onAutoPlay?: () => void;
  /** The "Translations are silent" hint shows now (RoomScreen decides when). */
  autoPlayHint?: boolean;
  onDismissHint?: () => void;
  /** UI-C29: the speaking seat the Listen control shows activity for (its label and colour slot), or null: none. */
  listenActivity?: { name: string; colour: number } | null;
  /** UI-C29: the first-time hint shows now (only ever with an activity). */
  listenHint?: boolean;
  /** UI-C21: the listen mode, when interpreter mode is offered in this room (null: no pill, the bar as before). */
  listenMode?: ListenMode | null;
  onListenChoice?: () => void;
  /** D-43(2): what the live voices do under a spoken phrase here (iOS: always muted). */
  liveUnder?: LiveUnder;
  /** G13: the spend cap was met - "text only, voice on tap", quiet. */
  voiceCapped?: boolean;
  /** UI-C24: a spoken translation holds the mic gate (not a replay): the ring's line is the listening hint. */
  micListening?: boolean;
  ring: RingState;
  level: number;
  muted: boolean;
  onMute: () => void;
  listening: boolean;
  listenEnabled: boolean;
  /** Ruling 1 (D-38): the live voices are silent here because THIS phone is replaying (the gate holds). */
  livePaused: boolean;
  /** Ruling 2: a remote voice's play() was refused and waits for a tap. */
  tapToHear: boolean;
  onListen: () => void;
  seatCount: number;
  onSeats: () => void;
  onLeave: () => void;
}) {
  const { lang, t } = useI18n();
  const listeningHint = micListening && ring === "playing";
  const message = listeningHint ? t("micListening") : ring in RING_MESSAGE ? t(RING_MESSAGE[ring as keyof typeof RING_MESSAGE]) : null;
  const otherLang = lang === "ar" ? "en" : "ar";
  const followsLevel = ring === "hearing" || ring === "heard" || ring === "unheard" || ring === "idle";
  // T-162: the one line that keeps a seat with no microphone from sending mute/unmute (the ring decides, so it follows
  // the permission both ways)
  const micBlocked = ring === "denied" || ring === "unavailable";
  const style = followsLevel ? ({ "--lvl": Math.min(1, level * 4).toFixed(2) } as CSSProperties) : undefined;
  return (
    <div className="bottom" data-spec="UI-C10 UI-P2">
      {tapToHear && (
        // a real button, so the tap that unlocks audio lands on something that says what it does; the unlock
        // itself is the app's document-level gesture listener (any tap anywhere works the same)
        <button type="button" className="tap-to-hear" data-spec="D-22 UI-C10">
          {t("tapToHear")}
        </button>
      )}
      {message && listeningHint && (
        // UI-C24: one hint, in both languages (the mock-up), the interface's first
        <p className="mic-status mic-listening" id="mic-status" role="status" data-spec="UI-C24 EM-25 EM-26 D-43">
          <span lang={lang}>{message}</span>
          <span lang={otherLang} dir={dirOf(otherLang)}>
            {otherLang === "ar" ? AR_BESIDE_EN.micListening : en.micListening}
          </span>
        </p>
      )}
      {message && !listeningHint && (
        <p className={`mic-status ring-${ring}`} id="mic-status" role="status" data-spec="UI-C19 EM-12">
          {message}
        </p>
      )}
      {playbackUnavailable && passive && !message && (
        // MAJLIS-140 (condition 5): the mute line's slot and style, describing the switch; the microphone's line
        // outranks it, and it outranks the silent-translations hint
        <p className="mic-status playback-unavailable" id="playback-status" role="status" data-spec="UI-C39 D-59 P-39">
          {t("playbackUnavailable")}
        </p>
      )}
      {autoPlayHint && !message && !(playbackUnavailable && passive) && (
        // MAJLIS-137 (UI-C39): the mute line's slot and style, with a close; the microphone's own line outranks it
        <p className="mic-status autoplay-hint" role="status" data-spec="UI-C39 D-57">
          <span>{t("autoPlayHint")}</span>
          <button type="button" className="icon-button" aria-label={t("close")} onClick={onDismissHint}>
            <CloseIcon />
          </button>
        </p>
      )}
      {livePaused && (
        <p className="mic-status live-paused" role="status" data-spec="D-38 UI-C19">
          {t("livePaused")}
        </p>
      )}
      {(listenMode !== null || voiceCapped) && (
        <div className="listen-row" data-spec="UI-C21 UI-C10 D-43">
          {listenMode !== null && (
            <button
              type="button"
              className="listen-mode"
              aria-haspopup="dialog"
              aria-label={t("listenChoice", { mode: t(listenMode === "translated" ? "listenTranslated" : "listenOriginal") })}
              onClick={onListenChoice}
              data-spec="UI-C21 D-43"
            >
              <span className="listen-pill">
                <SpeakerIcon />
                <span className={`mode ${listenMode}`}>
                  {t(listenMode === "translated" ? "listenTranslated" : "listenOriginal")}
                </span>
              </span>
            </button>
          )}
          {voiceCapped ? (
            <span className="duck capped" role="status" data-spec="D-43">
              {t("voiceCapped")}
            </span>
          ) : (
            listenMode === "translated" && (
              <span className="duck" data-spec="D-43 UI-C21">
                {t("liveUnder", { state: "\u0000" })
                  .split("\u0000")
                  .flatMap((part, i) => (i === 0 ? [part] : [<em key="s">{t(liveUnder === "muted" ? "liveMuted" : "liveLowered")}</em>, part]))}
              </span>
            )
          )}
        </div>
      )}
      {listenActivity && listenHint && (
        <p className="listen-hint needs-tap" aria-hidden="true" data-spec="UI-C29">
          {t(shown("listenHintTap") ?? "listenHint", { control: t("listen") })}
        </p>
      )}
      <nav className="bottom-bar" aria-label={t("appName")}>
        <div className={passive ? "bar-side autoplay-side" : "bar-side"}>
          {passive ? (
            autoPlay !== null && (
              <button
                type="button"
                className={`listen-mode autoplay ui-${lang}`}
                role="switch"
                aria-checked={autoPlay}
                aria-label={t("autoPlay")}
                aria-describedby={playbackUnavailable && !message ? "playback-status" : undefined}
                onClick={onAutoPlay}
                data-spec="UI-C39 D-57 D-08"
              >
                <span className={autoPlay ? "listen-pill" : "listen-pill off"}>
                  {autoPlay ? <SpeakerIcon /> : <SpeakerOffIcon />}
                  <span className={`mode ${autoPlay ? "translated" : "original"}`} aria-hidden="true">
                    <span className="ap-full">{t("autoPlay")}</span>
                    <span className="ap-short">{t("autoPlayShort")}</span>
                  </span>
                </span>
              </button>
            )
          ) : (
          <>
          <span className="bar-item">
          <button
            type="button"
            className={`round-button listen${listenActivity ? ` activity seat-c${listenActivity.colour}` : ""}${listenActivity && listenHint ? " tap-ring" : ""}`}
            aria-pressed={listening}
            aria-label={listenActivity ? t("listenActivity", { name: listenActivity.name }) : t("listen")}
            disabled={!listenEnabled}
            onClick={onListen}
            data-spec="UI-C10 UI-C29 P-17 D-22 EM-6"
          >
            {listening ? <SpeakerIcon /> : listenActivity ? <SpeakerOffActivityIcon /> : <SpeakerOffIcon />}
          </button>
          <span className="bar-label" aria-hidden="true">
            {t("listen")}
          </span>
          </span>
          <span className="bar-item">
          <button
            type="button"
            className="round-button count-button"
            aria-label={t("people", { n: seatCount })}
            aria-haspopup="dialog"
            onClick={onSeats}
            data-spec="UI-C10 UI-C8"
          >
            <SeatsIcon />
          </button>
          <span className="bar-label" aria-hidden="true">
            {t("people", { n: seatCount })}
          </span>
          </span>
          </>
          )}
        </div>
        <span className="bar-item mic-item">
        <button
          type="button"
          className={`mic ring-${ring}`}
          aria-pressed={muted}
          aria-label={ring === "stopped" ? t("micStoppedLabel") : t("mute")}
          aria-describedby={message ? "mic-status" : undefined}
          aria-disabled={micBlocked || undefined}
          onClick={micBlocked ? undefined : onMute}
          style={style}
          data-ring={ring}
          data-spec="UI-C10 UI-C19 P-3 EM-12 T-162"
        >
          {muted || ring === "denied" || ring === "unavailable" || ring === "stopped" ? <MicOffIcon /> : <MicIcon />}
        </button>
        <span className="bar-label" aria-hidden="true">
          {/* MAJLIS-163 (the consultant, 2026-10-04): the label follows the action - Mute while live, Unmute while muted.
              MAJLIS-167 (item 2): stopped, denied and unavailable show NO word (the status line above already says it);
              the empty label keeps the slot, as Leave's does, so the mic does not move */}
          {micBlocked || ring === "stopped" ? "\u00a0" : muted ? t("unmuteLabel") : t("muteLabel")}
        </span>
        </span>
        <div className="bar-side end">
          <span className="bar-item">
            {/* the word IS the accessible name; the empty label below keeps the pill level with the discs */}
            <button type="button" className="leave-pill leave" onClick={onLeave} data-spec="UI-C10 P-3 D-15">
              <HangUpIcon />
              <span>{t("leave")}</span>
            </button>
            <span className="bar-label" aria-hidden="true">
              {"\u00a0"}
            </span>
          </span>
        </div>
      </nav>
    </div>
  );
}
